import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { intelligenceRecords } from '@cwos/api-client';
import { Pill, SkeletonSet } from '@cwos/ui-admin';
import { localDayString } from '../../../lib/timeFormat.js';
import { LoadFailure } from '../../LoadFailure.js';
import {
  consultedLine,
  evidenceLine,
  outcomeSentence,
  strengthLabel,
  strengthTone,
} from './recordsLogic.js';

/**
 * ONE RECOMMENDATION: what was advised, what it rested on, and what happened.
 *
 * ===========================================================================
 * THE THREE THINGS THIS PAGE IS FOR, AND THE ORDER THEY ARE IN
 * ===========================================================================
 *   1. The advice, and whether it was ever approved. `confirmation === null`
 *      means NOTHING WAS APPROVED, which is a recommendation's normal state and
 *      the state the whole design defends — so it is stated, not omitted.
 *   2. How much the evidence actually supports, with the caveat the server
 *      computed. The band and its honesty about itself travel together; there
 *      is no route that returns one without the other and this page does not
 *      render one without the other.
 *   3. What happened afterwards. This is the `outcome:read` half, and it is the
 *      reason that key was kept rather than removed: without it the same screen
 *      could only report an absence it had not established.
 *
 * ===========================================================================
 * THE MANIFEST IS A MANIFEST
 * ===========================================================================
 * `evidence[]` carries the tool, the scope permission, a row count, a content
 * hash and a PARAMETER COUNT. The recorded call parameters deliberately do not
 * cross the boundary. The count is rendered — "3 parameters, not shown" — so
 * the omission is visible rather than silent; there is no control that expands
 * it, because there is no route that would answer.
 *
 * ===========================================================================
 * NO FEEDBACK CONTROL LIVES HERE
 * ===========================================================================
 * Filing a signal is `recommendation:feedback`, which migration 2018000000000
 * removed from the seeded `analyst` role — the ruling reserves consequential
 * authority to a separate explicit grant — and on the outcome axis it derives
 * PERMANENT outcome memory. A control that files that belongs on a screen built
 * for the operator who received the advice, with the outcome-axis signals
 * separated from the disposition ones so nobody files a permanent claim by
 * pressing the nearest button. This page is the READ, and it says so rather
 * than offering a control that would 403 for the persona it was surfaced for.
 */
export function RecommendationRecordDetail() {
  const { recommendationId } = useParams({ strict: false }) as { recommendationId: string };
  const detail = useQuery({
    queryKey: ['intelligence', 'recommendations', recommendationId],
    queryFn: () => intelligenceRecords.getRecommendation(recommendationId),
  });

  if (detail.isLoading) return <SkeletonSet surface="drawer" rows={8} />;
  if (detail.isError) {
    return (
      <LoadFailure
        error={detail.error}
        message="We could not load this recommendation."
        onRetry={() => void detail.refetch()}
        mainRead
        refusedHeading="You cannot open this recommendation"
      />
    );
  }
  const data = detail.data;
  if (!data) return null;

  const { recommendation, confidence, evidence, doctrine, feedback, confirmation, outcome } = data;

  return (
    <article className="rn-col" style={{ maxWidth: 900 }}>
      <header className="rn-col">
        <h1>{recommendation.headline}</h1>
        <p className="rn-muted">{recommendation.question}</p>
        <p className="rn-muted">
          About {recommendation.subject} · recorded {recommendation.created_at.slice(0, 10)} ·
          framework {recommendation.framework_key} v{recommendation.framework_version}
        </p>
      </header>

      {recommendation.no_feasible_action ? (
        <p className="rn-muted">
          This record declared NO FEASIBLE ACTION. That is an answer, not a missing one — Phibian
          considered the situation and had nothing to advise.
        </p>
      ) : null}

      {recommendation.tradeoffs ? (
        <section className="rn-col">
          <h2>Trade-offs</h2>
          <p>{recommendation.tradeoffs}</p>
        </section>
      ) : null}

      <section className="rn-col" aria-labelledby="confidence-heading">
        <h2 id="confidence-heading">How much this rests on</h2>
        <Pill variant={strengthTone(confidence.strength)}>
          {strengthLabel(confidence.strength)}
        </Pill>
        <p>{confidence.caveat}</p>
        {confidence.basis.length > 0 ? (
          <ul>
            {confidence.basis.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}
        {confidence.limits.length > 0 ? (
          <>
            <h3>Limits</h3>
            <ul>
              {confidence.limits.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </>
        ) : null}
        {/* WHETHER THE OUTCOME STORE WAS CONSULTED is part of the band, not a
            footnote: a band computed without it is a different claim. */}
        <p className="rn-muted">{consultedLine(confidence.outcome.consulted)}</p>
      </section>

      <section className="rn-col" aria-labelledby="evidence-heading">
        <h2 id="evidence-heading">What it read</h2>
        {evidence.length === 0 ? (
          <p className="rn-muted">
            This record cites no retrieval. It can only be a declared no-feasible-action, which the
            server refuses to record any other way.
          </p>
        ) : (
          <ul>
            {evidence.map((row) => (
              <li key={row.id}>{evidenceLine(row)}</li>
            ))}
          </ul>
        )}
      </section>

      <section className="rn-col" aria-labelledby="doctrine-heading">
        <h2 id="doctrine-heading">What it was composed under</h2>
        {doctrine.length === 0 ? (
          <p className="rn-muted">
            No doctrine rule was in force for this composition. The corpus was asked and answered
            with nothing — this is not a failure to ask.
          </p>
        ) : (
          <ul>
            {doctrine.map((rule) => (
              <li key={rule.id}>
                {rule.subject} · {rule.bindingness} · {rule.authority} · as of {rule.as_of_date} ·{' '}
                {rule.disposition}
              </li>
            ))}
          </ul>
        )}
        {recommendation.unresolved_conflict_count > 0 ? (
          <p className="rn-muted">
            {recommendation.unresolved_conflict_count} equal-standing contradiction
            {recommendation.unresolved_conflict_count === 1 ? '' : 's'} could not be decided by
            precedence and {recommendation.unresolved_conflict_count === 1 ? 'was' : 'were'}{' '}
            surfaced rather than silently resolved.
          </p>
        ) : null}
      </section>

      <section className="rn-col" aria-labelledby="outcome-heading">
        <h2 id="outcome-heading">What happened</h2>
        {/* The whole point of `outcome:read`. Six states, six sentences, and
            none of them is a zero. */}
        <p>{outcomeSentence(outcome)}</p>
        {outcome.state === 'measured' ? (
          <ul>
            {outcome.outcomes.map((entry) => (
              <li key={entry.outcomeRecordId}>
                {entry.signal} · observed {localDayString(new Date(entry.observedAtMs))} ·{' '}
                {entry.explicit ? 'stated' : 'inferred'}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="rn-col" aria-labelledby="decision-heading">
        <h2 id="decision-heading">Approval and signals</h2>
        <p className="rn-muted">
          {confirmation === null
            ? 'Nothing here was approved. That is the ordinary state of a recommendation, not a gap in this record.'
            : `Approved on ${confirmation.confirmed_at.slice(0, 10)}.`}
        </p>
        {feedback.length === 0 ? (
          <p className="rn-muted">
            No signal has been filed against this recommendation. Filing one needs
            <code> recommendation:feedback</code>, which is a separate authority from reading the
            record.
          </p>
        ) : (
          <ul>
            {feedback.map((row) => (
              <li key={row.id}>
                {row.signal} ({row.signal_axis}) · recorded {row.recorded_at.slice(0, 10)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </article>
  );
}
